import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DataTable } from "@/modules/shell/data-table";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { RemindMeButton } from "@/modules/shell/RemindMeButton";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { PosReceipt, printPosReceipt } from "@/modules/pos/receipt/PosReceipt";
import { buildReceipt } from "@/modules/pos/receipt/receipt-math";
import { getPosTicket, voidPosTicket } from "@/shared/api/pos";
import { listReturns } from "@/shared/api/returns";
import { queryKeys } from "@/shared/query/keys";
import {
  posTicketLineColumns,
  posTicketReturnColumns,
  posTenderColumns,
} from "./pos-ticket-detail-columns";

const TICKET_DOC_TABS = ["ticket", "returns"] as const;

export function PosTicketDetailPage() {
  const { companySlug, ticketId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getPosTicket>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [docTab, setDocTab] = useDeskDocTab("ticket", TICKET_DOC_TABS);

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !ticketId) return;
    getPosTicket(companySlug, ticketId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, ticketId, navigate]);

  const relatedReturnsQuery = useQuery({
    queryKey: queryKeys.returns.list(companySlug ?? "", {
      sourceType: "pos_ticket",
      sourceId: ticketId,
      limit: 100,
    }),
    enabled: Boolean(companySlug && ticketId),
    queryFn: () =>
      listReturns(companySlug!, {
        sourceType: "pos_ticket",
        sourceId: ticketId!,
        limit: 100,
      }),
  });

  useRecordRecentDoc({
    companySlug,
    kind: "posTicket",
    id: data?.ticket.id,
    label: data?.ticket.number,
    path:
      companySlug && data?.ticket.id
        ? `/c/${companySlug}/pos/tickets/${data.ticket.id}`
        : undefined,
    hint: data?.ticket.terminalName,
  });

  const receiptData = useMemo(() => {
    if (!data) return null;
    const { ticket, lines, tenders, company } = data;
    const tender = tenders[0];
    return buildReceipt({
      companyName: company.name,
      terminalName: ticket.terminalName,
      number: ticket.number,
      issuedAt: ticket.postedAt ?? ticket.createdAt,
      lines: lines.map((l) => ({
        name: l.description ?? l.productName,
        qty: l.qty,
        unitPrice: l.unitPrice,
      })),
      discountPct:
        Number(ticket.discountPct) > 0 ? ticket.discountPct : null,
      taxRatePct: ticket.taxRatePct,
      taxPriceMode: ticket.taxPriceMode,
      payMethodName: tender?.payMethodName ?? "—",
      tenderAmount: tender?.amount ?? ticket.grossTotal,
      changeGiven: tender?.changeGiven ?? "0",
      netTotal: ticket.netTotal,
      taxTotal: ticket.taxTotal,
      grossTotal: ticket.grossTotal,
      currencyCode: company.currencyCode,
    });
  }, [data]);

  if (!companySlug || !ticketId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading ticket…</Alert>
        )}
      </PageFrame>
    );
  }

  const { ticket, lines, tenders } = data;
  const ticketPath = `/c/${companySlug}/pos/tickets/${ticketId}`;
  const registerPath = `/c/${companySlug}/b/${ticket.branchSlug}/pos/t/${ticket.terminalId}`;
  const canReturnOrVoid = ticket.lifecycle === "posted";
  const relatedReturns = relatedReturnsQuery.data?.returns ?? [];

  async function onVoid() {
    if (!companySlug || !ticketId) return;
    const ok = await confirmUi({
      title: `Void ${ticket.number}?`,
      description:
        "Stock will restock. Voiding is permanent — you cannot undo this ticket. Use Return if the customer brought goods back.",
      confirmLabel: "Void ticket",
      destructive: true,
    });
    if (!ok) return;
    setVoidBusy(true);
    setError(null);
    try {
      await voidPosTicket(companySlug, ticketId, {
        reason: "desk void",
        idempotencyKey: `void-${ticketId}`,
      });
      const refreshed = await getPosTicket(companySlug, ticketId);
      setData(refreshed);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setVoidBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      {receiptData && (
        <div className="pos-receipt-print" aria-hidden>
          <PosReceipt data={receiptData} />
        </div>
      )}
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/pos/tickets`}
            listLabel="POS tickets"
          />
        </div>
        <PageHeader
          eyebrow="POS ticket"
          title={ticket.number}
          description={[ticket.terminalName, ticket.branchName]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              {companySlug && ticketId ? (
                <RemindMeButton
                  companySlug={companySlug}
                  kind="pos_ticket"
                  docId={ticketId}
                  docNumber={ticket.number}
                  partyName={ticket.terminalName}
                  disabled={voidBusy}
                />
              ) : null}
              {receiptData ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => printPosReceipt()}
                >
                  Print receipt
                </Button>
              ) : null}
              {canReturnOrVoid ? (
                <Button type="button" variant="outline" size="sm" asChild>
                  <Link
                    to={`/c/${companySlug}/returns/new?sourceType=pos_ticket&sourceId=${ticketId}`}
                    className="no-underline"
                  >
                    Return
                  </Link>
                </Button>
              ) : null}
              {canReturnOrVoid ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={voidBusy}
                  onClick={() => void onVoid()}
                >
                  {voidBusy ? "Voiding…" : "Void"}
                </Button>
              ) : null}
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={ticket.lifecycle} showLabel />
        </div>
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="pos_ticket"
          docId={ticketId}
          linkPath={ticketPath}
          docLabel={[ticket.number, ticket.terminalName]
            .filter(Boolean)
            .join(" · ")}
        />
        <DeskDocTabs
          aria-label="POS ticket views"
          value={docTab}
          onChange={setDocTab}
          items={[
            { id: "ticket", label: "Ticket" },
            {
              id: "returns",
              label: "Returns",
              count: relatedReturns.length,
            },
          ]}
        />
      </div>

      {docTab === "ticket" && (
        <>
          <FieldGroup title="Money" className="mt-6" level="sub">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[var(--color-muted)]">Net</dt>
              <dd className="m-0 font-mono tabular-nums">{ticket.netTotal}</dd>
              <dt className="text-[var(--color-muted)]">Tax</dt>
              <dd className="m-0 font-mono tabular-nums">{ticket.taxTotal}</dd>
              <dt className="text-[var(--color-muted)]">Gross</dt>
              <dd className="m-0 font-mono tabular-nums font-medium">
                {ticket.grossTotal}
              </dd>
              {Number(ticket.discountPct) > 0 ||
              Number(ticket.discountAmount) > 0 ? (
                <>
                  <dt className="text-[var(--color-muted)]">Discount</dt>
                  <dd className="m-0 font-mono tabular-nums text-xs">
                    {Number(ticket.discountPct) > 0
                      ? `${ticket.discountPct}%`
                      : null}
                    {Number(ticket.discountPct) > 0 &&
                    Number(ticket.discountAmount) > 0
                      ? " · "
                      : null}
                    {Number(ticket.discountAmount) > 0
                      ? ticket.discountAmount
                      : null}
                  </dd>
                </>
              ) : null}
            </dl>
          </FieldGroup>
          <FieldGroup title="Summary" className="mt-6" level="sub">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[var(--color-muted)]">Terminal</dt>
              <dd className="m-0">
                <Link to={registerPath} className="no-underline">
                  {ticket.terminalName}
                  <span className="font-mono text-xs text-[var(--color-muted)]">
                    {" "}
                    · {ticket.terminalCode}
                  </span>
                </Link>
              </dd>
              <dt className="text-[var(--color-muted)]">Branch</dt>
              <dd className="m-0">{ticket.branchName}</dd>
              {ticket.cashierLabel ? (
                <>
                  <dt className="text-[var(--color-muted)]">Cashier</dt>
                  <dd className="m-0">{ticket.cashierLabel}</dd>
                </>
              ) : null}
              {ticket.postedAt ? (
                <>
                  <dt className="text-[var(--color-muted)]">Posted</dt>
                  <dd className="m-0 text-xs">{ticket.postedAt}</dd>
                </>
              ) : null}
              {ticket.voidedAt ? (
                <>
                  <dt className="text-[var(--color-muted)]">Voided</dt>
                  <dd className="m-0 text-xs">{ticket.voidedAt}</dd>
                </>
              ) : null}
            </dl>
          </FieldGroup>
          {ticket.notes || ticket.syncStatus !== "synced" ? (
            <AdvancedSection className="no-print mt-4" subtitle="desk">
              <FieldGroup title="More" level="sub">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  {ticket.syncStatus !== "synced" ? (
                    <>
                      <dt className="text-[var(--color-muted)]">Sync</dt>
                      <dd className="m-0">{ticket.syncStatus}</dd>
                    </>
                  ) : null}
                  {ticket.notes ? (
                    <>
                      <dt className="text-[var(--color-muted)]">Notes</dt>
                      <dd className="m-0">{ticket.notes}</dd>
                    </>
                  ) : null}
                </dl>
              </FieldGroup>
            </AdvancedSection>
          ) : null}

          <DataTable
            listId="pos-ticket-lines"
            companySlug={companySlug}
            className="mt-6 border-t border-[var(--color-border)]/90 pt-4"
            columns={posTicketLineColumns()}
            data={lines}
            getRowId={(r) => r.id}
            manualSorting={false}
            empty={
              <EmptyState
                title="No lines"
                description="This ticket has no product lines."
              />
            }
          />

          {tenders.length > 0 ? (
            <DataTable
              listId="pos-ticket-tenders"
              companySlug={companySlug}
              className="mt-4"
              columns={posTenderColumns()}
              data={tenders}
              getRowId={(r) => r.id}
              manualSorting={false}
            />
          ) : null}
        </>
      )}

      {docTab === "returns" && (
        <div className="mt-2">
          {relatedReturnsQuery.isPending ? (
            <Alert variant="muted">Loading returns…</Alert>
          ) : (
            <DataTable
              listId="pos-ticket-returns"
              companySlug={companySlug}
              columns={posTicketReturnColumns(companySlug)}
              data={relatedReturns}
              getRowId={(r) => r.id}
              manualSorting={false}
              empty={
                <EmptyState
                  title="No returns yet"
                  description="Start a return from this ticket if the customer brings goods back."
                  action={
                    canReturnOrVoid ? (
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to={`/c/${companySlug}/returns/new?sourceType=pos_ticket&sourceId=${ticketId}`}
                          className="no-underline"
                        >
                          New return
                        </Link>
                      </Button>
                    ) : null
                  }
                />
              }
            />
          )}
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="pos_ticket"
        docId={ticketId}
        linkPath={ticketPath}
      />
    </PageFrame>
  );
}
